/*
PURPOSE: Editorial (base1) homepage hero for the home page. Serif three-beat headline,
muted editorial palette, terracotta manifesto link, the real
AllInOneCoralCreator, and three pre-toned editorial hero photos that rotate.
USE CASES:
- Top section of the home page. Replaces the legacy dark photographic hero with the
  editorial treatment and copy.
DOMAIN BOUNDARIES:
- Presentation + the existing /manifesto navigation only. The headline and
  subcopy WORDS are verbatim from publicPages.home.hero.title/subtitle; the
  explicit <br/> line breaks are an intentional editorial wrapping treatment
  (ported from the design sandbox's tuned hero). Keep the words in sync with
  publicPages on any future copy edit — no fabricated/changed words.
- A soft HexBloom accent behind the headline is purely DECORATIVE
  (aria-hidden, a fixed decorative seed — not real contribution data, not a
  data claim), mirroring the sandbox hero's ornamental cloud.
- Reuses the production AllInOneCoralCreator and HexBloom. No imports from
  design-sandbox/.
- The scroll-to-creator target ref is owned by the home page and passed in so later
  section CtaPills can scroll here, mirroring the legacy Home.
ASSUMPTIONS:
- The hero creator is desktop-only (md+), matching the legacy Home where the
  mobile creator lives in the How-It-Works section.
*/

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import HexBloom from '../../../components/HexBloom/HexBloom';
import EditorialCreatorCard from './EditorialCreatorCard';
import heroProducer from '../../../assets/images/hero-producer.jpg';
import heroSinger from '../../../assets/images/hero-singer.jpg';
import heroGuitarists from '../../../assets/images/hero-guitarists.jpg';

// Three pre-toned hero photos, rotated on a slow cross-fade. Module-scope so
// the array has a stable identity across renders (rotation effect deps stay
// honest). All are 1200x1800 (2:3 portrait), duotoned in the editorial
// palette. On lg+ the creator card covers the right two thirds of the frame,
// so each subject's face must sit in the left third of the crop.
const HERO_PHOTOS = [
  {
    src: heroProducer,
    alt: 'A producer silhouetted against stage light, performing for a small live audience',
  },
  {
    src: heroSinger,
    alt: 'A singer mid-song at the microphone under stage lights',
  },
  {
    src: heroGuitarists,
    alt: 'A guitarist grinning mid-song in a poster-covered room, a bandmate beside them',
  },
];

interface HeroProps {
  creatorRef?: React.RefObject<HTMLDivElement>;
}

const Hero: React.FC<HeroProps> = ({ creatorRef }) => {
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);

  // Slow cross-fade between the hero photos every 10s.
  useEffect(() => {
    const timer = setTimeout(() => {
      setActiveImage((prev) => (prev + 1) % HERO_PHOTOS.length);
    }, 10000);
    return () => clearTimeout(timer);
  }, [activeImage]);

  return (
    <section className="bg-ivory">
      <div
        ref={creatorRef}
        className="max-w-screen-xl 2xl:max-w-[1440px] 3xl:max-w-[1600px] mx-auto px-6 md:px-12 pt-14 md:pt-24 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-10 items-start"
      >
        {/* Left: editorial serif headline + body + manifesto link */}
        <div className="lg:col-span-6 relative">
          {/* Decorative ornamental bloom behind the headline (aria-hidden,
              a fixed decorative seed — not real contribution data). */}
          <div
            className="absolute top-8 md:top-16 right-0 md:right-2 lg:-right-12 pointer-events-none select-none opacity-85 z-0"
            aria-hidden="true"
          >
            <HexBloom seed={4} size={360} />
          </div>

          {/* Words verbatim from publicPages.home.hero.title; the <br/> are an
              intentional editorial line-break treatment (sandbox-tuned). */}
          <h1
            className="relative z-10 font-cormorant text-editorial-muted font-normal text-[40px] leading-[1.04] md:text-[60px] md:leading-[1.02]"
            style={{ letterSpacing: '-0.01em' }}
          >
            Artists you love.<br />
            Money that<br />
            reaches them.<br />
            Community<br />
            that gets it.
          </h1>

          {/* Words verbatim from publicPages.home.hero.subtitle; editorial
              line-break treatment (sandbox-tuned). */}
          <p className="relative z-10 mt-9 max-w-prose text-[15px] md:text-[15px] text-editorial-muted leading-[1.55]">
            Back artists you choose.<br />
            One monthly contribution, split your way.<br />
            The community? People who care<br />
            enough to back music&apos;s future.
          </p>

          <button
            onClick={() => navigate('/manifesto')}
            className="relative z-10 inline-flex items-center gap-3 mt-8 text-[13px] tracking-[0.18em] uppercase text-editorial-accent hover:text-editorial-accent/80 transition-colors"
          >
            <span>READ OUR MANIFESTO</span>
            <ArrowRight size={17} strokeWidth={1.75} />
          </button>
        </div>

        {/* Right: the rotating hero photo + the creator card hovering over
            its right edge (the base1 overlap composition). The photos
            cross-fade every 10s; all are pre-toned in the editorial palette
            (duotone). Frame is 4:5 — portrait without dominating;
            object-cover trims a little top + bottom from the 1200x1800 source. */}
        <div className="lg:col-span-6 relative">
          {/* Photo: full width below lg; narrowed on lg+ to leave room for the
              creator card to hang off the right edge (base1 hang). */}
          <div className="relative overflow-hidden rounded-2xl aspect-[4/5] bg-cream shadow-[0_24px_50px_-24px_rgba(58,42,30,0.35)] lg:w-[72%]">
            {HERO_PHOTOS.map((photo, i) => (
              <img
                key={i}
                src={photo.src}
                alt={photo.alt}
                aria-hidden={activeImage !== i}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
                  activeImage === i ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
          </div>

          {/* Creator card.
              - below md: hidden (mobile creator lives in How-It-Works).
              - md to lg: stacked under the photo (legacy behaviour).
              - lg+: absolutely positioned, hovering over the photo's right
                edge — the base1 hero composition. */}
          <div className="hidden md:block mt-8 lg:mt-0 lg:absolute lg:right-0 lg:top-12 lg:w-[60%] lg:min-w-[400px] xl:min-w-[440px]">
            <EditorialCreatorCard />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
